import { Link } from 'react-router'
import { Card, Skeleton } from '@/components/ui'
import type { LucideIcon } from 'lucide-react'

export interface TileProps {
  id: string
  labelKey: string
  label: string
  value: string | number
  icon?: LucideIcon
  tone?: 'success' | 'warning' | 'error' | 'neutral'
  'data-testid'?: string
  /** When set, the tile becomes a React Router Link to this path. */
  href?: string
}

interface KpiTileGridProps {
  tiles: TileProps[]
  isLoading?: boolean
  /** 4 (default): two tiles a row on a phone, four from `sm`. 2: always two,
   * for a pair of figures that sits beside another pair on a wide screen. */
  columns?: 2 | 4
}

const toneClasses: Record<NonNullable<TileProps['tone']>, string> = {
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',
  neutral: 'text-base-content',
}

export default function KpiTileGrid({ tiles, isLoading = false, columns = 4 }: KpiTileGridProps) {
  return (
    <div className={`grid grid-cols-2 ${columns === 4 ? 'sm:grid-cols-4' : ''} gap-2 sm:gap-4`}>
      {tiles.map((tile) => {
        const Icon = tile.icon
        const colorClass = tile.tone ? toneClasses[tile.tone] : 'text-base-content'
        const tileContent = (
          <>
            {Icon && (
              <div className={`mb-1 ${colorClass}`}>
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            )}
            <p className="text-xs sm:text-sm text-base-content/60 truncate">{tile.label}</p>
            {isLoading ? (
              <Skeleton className="h-7 w-16 mt-1" />
            ) : (
              <p
                className={`text-lg sm:text-2xl font-bold mt-0.5 truncate ${colorClass}`}
                // A plain tile names its figure for a screen reader. A linked
                // tile does not: the link already reads "label figure", and
                // this would be read into its name a second time.
                aria-label={tile.href ? undefined : `${tile.label}: ${tile.value}`}
              >
                {tile.value}
              </p>
            )}
          </>
        )

        if (tile.href) {
          return (
            <Card
              key={tile.id}
              as={Link}
              to={tile.href}
              noBody
              className="bg-base-100 shadow-sm p-3 sm:p-4 min-w-0 hover:bg-base-200 transition-colors no-underline"
              data-testid={tile['data-testid']}
            >
              {tileContent}
            </Card>
          )
        }

        return (
          <Card
            key={tile.id}
            noBody
            className="bg-base-100 shadow-sm p-3 sm:p-4 min-w-0"
            data-testid={tile['data-testid']}
          >
            {tileContent}
          </Card>
        )
      })}
    </div>
  )
}
